import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Row, Spinner, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchPublicBlogPosts } from 'helpers/blog';

const MAX_PREVIEW_ITEMS = 6;
const MAX_FEATURED_ITEMS = 2;

const getLineClampStyle = lines => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden'
});

const LandingBlog = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await fetchPublicBlogPosts();
        setPosts(response.posts || []);
      } catch (error) {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  if (loading) {
    return (
      <div className="py-6 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!posts.length) {
    return null;
  }

  const featuredPosts = posts.filter(post => post.is_featured).slice(0, MAX_FEATURED_ITEMS);
  const featuredIds = new Set(featuredPosts.map(post => post.id));
  const latestPosts = posts.filter(post => !featuredIds.has(post.id));
  const previewPosts = [...featuredPosts, ...latestPosts].slice(0, MAX_PREVIEW_ITEMS);

  return (
    <section id="blog" className="py-6 bg-light">
      <Container>
        <div className="text-center mb-5">
          <h2 className="mb-2">{t('BLOG.LANDING_TITLE')}</h2>
          <p className="text-600 mb-0">{t('BLOG.LANDING_DESCRIPTION')}</p>
        </div>
        <Row className="g-4">
          {previewPosts.map(post => (
            <Col lg={4} md={6} key={post.id}>
              <Card className="h-100 border">
                <div className="ratio ratio-4x3">
                  {post.cover_image_url ? (
                    <img
                      src={post.cover_image_url}
                      alt={post.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${post.accent_color || '#0b5ed7'}, #0b1727)` }} />
                  )}
                </div>
                <Card.Body className="d-flex flex-column">
                  <div className="text-500 fs--2 mb-2">
                    {[post.category, post.author_name || post.author?.full_name || 'Gestodo', post.reading_time].filter(Boolean).join(' · ')}
                  </div>
                  <h5 className="mb-3" style={getLineClampStyle(3)}>{post.title}</h5>
                  {post.subtitle ? (
                    <p className="text-700 mb-2" style={getLineClampStyle(2)}>
                      {post.subtitle}
                    </p>
                  ) : null}
                  <p className="text-600 mb-0" style={getLineClampStyle(4)}>
                    {post.excerpt || t('BLOG.LANDING_FALLBACK')}
                  </p>
                  <div className="mt-auto pt-3">
                    <Button as={Link} to={`/blog/${post.slug}`} variant="outline-primary" size="sm">
                      {t('BLOG.READ_POST')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <div className="text-center mt-5">
          <Button as={Link} to="/blog" variant="primary">
            {t('BLOG.VIEW_ALL_PUBLIC')}
          </Button>
        </div>
      </Container>
    </section>
  );
};

export default LandingBlog;
